/**
 * Analytics Report Email Template
 * Weekly analytics summary for game studios
 * Solis Games - Phase 2.2 V3
 */

export const generateAnalyticsReportHTML = (data) => {
  const {
    studio_name,
    user_name,
    report_period,
    games_summary,
    top_game,
    total_players,
    total_sessions,
    avg_session_duration,
    retention_summary,
    peak_hours,
    insights,
    dashboard_url
  } = data;

  return `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Weekly Analytics Report - ${studio_name}</title>
  <style>
    body {
      margin: 0;
      padding: 0;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
      background-color: #0f172a;
      color: #e2e8f0;
    }
    .container {
      max-width: 600px;
      margin: 0 auto;
      background-color: #1e293b;
    }
    .header {
      background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
      padding: 40px 30px;
      text-align: center;
    }
    .header h1 {
      margin: 0;
      color: white;
      font-size: 28px;
      font-weight: 700;
    }
    .header p {
      margin: 10px 0 0 0;
      color: rgba(255, 255, 255, 0.9);
      font-size: 16px;
    }
    .content {
      padding: 30px;
    }
    .greeting {
      margin-bottom: 25px;
      font-size: 16px;
      line-height: 1.6;
      color: #cbd5e1;
    }
    .stat-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 15px;
      margin: 25px 0;
    }
    .stat-card {
      background: linear-gradient(135deg, #334155 0%, #1e293b 100%);
      border: 1px solid #334155;
      border-radius: 12px;
      padding: 20px;
      text-align: center;
    }
    .stat-label {
      font-size: 12px;
      color: #94a3b8;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      margin-bottom: 8px;
    }
    .stat-value {
      font-size: 32px;
      font-weight: 700;
      color: #6366f1;
      margin: 0;
    }
    .stat-subtext {
      font-size: 12px;
      color: #64748b;
      margin-top: 4px;
    }
    .section {
      margin: 30px 0;
      background-color: #0f172a;
      border: 1px solid #334155;
      border-radius: 12px;
      padding: 20px;
    }
    .section-title {
      font-size: 18px;
      font-weight: 700;
      color: #e2e8f0;
      margin: 0 0 15px 0;
      display: flex;
      align-items: center;
      gap: 8px;
    }
    .game-item {
      background-color: #1e293b;
      border: 1px solid #334155;
      border-radius: 8px;
      padding: 15px;
      margin-bottom: 12px;
    }
    .game-name {
      font-size: 16px;
      font-weight: 600;
      color: #e2e8f0;
      margin-bottom: 8px;
    }
    .game-stats {
      display: flex;
      gap: 20px;
      font-size: 13px;
      color: #94a3b8;
    }
    .insight-card {
      background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
      border-left: 4px solid #6366f1;
      border-radius: 8px;
      padding: 16px;
      margin-bottom: 12px;
    }
    .insight-title {
      font-size: 14px;
      font-weight: 600;
      color: #e2e8f0;
      margin-bottom: 6px;
    }
    .insight-message {
      font-size: 13px;
      color: #cbd5e1;
      line-height: 1.5;
    }
    .insight-success {
      border-left-color: #10b981;
    }
    .insight-warning {
      border-left-color: #f59e0b;
    }
    .insight-info {
      border-left-color: #3b82f6;
    }
    .peak-hours-box {
      background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
      border-radius: 8px;
      padding: 20px;
      text-align: center;
      margin: 15px 0;
    }
    .peak-hours-label {
      font-size: 12px;
      color: rgba(255, 255, 255, 0.8);
      text-transform: uppercase;
      letter-spacing: 0.5px;
      margin-bottom: 8px;
    }
    .peak-hours-time {
      font-size: 24px;
      font-weight: 700;
      color: white;
      margin: 0;
    }
    .peak-hours-day {
      font-size: 14px;
      color: rgba(255, 255, 255, 0.9);
      margin-top: 4px;
    }
    .cta-button {
      display: inline-block;
      background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
      color: white;
      text-decoration: none;
      padding: 14px 32px;
      border-radius: 8px;
      font-weight: 600;
      font-size: 15px;
      margin: 25px 0;
      text-align: center;
    }
    .footer {
      background-color: #0f172a;
      padding: 30px;
      text-align: center;
      border-top: 1px solid #334155;
    }
    .footer-text {
      font-size: 13px;
      color: #64748b;
      line-height: 1.6;
      margin: 8px 0;
    }
    .footer-link {
      color: #6366f1;
      text-decoration: none;
    }
    .badge {
      display: inline-block;
      padding: 4px 10px;
      border-radius: 12px;
      font-size: 11px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
    .badge-success {
      background-color: #10b98120;
      color: #10b981;
    }
    .badge-warning {
      background-color: #f59e0b20;
      color: #f59e0b;
    }
    @media only screen and (max-width: 600px) {
      .stat-grid {
        grid-template-columns: 1fr;
      }
      .header h1 {
        font-size: 24px;
      }
      .stat-value {
        font-size: 28px;
      }
    }
  </style>
</head>
<body>
  <div class="container">
    <!-- Header -->
    <div class="header">
      <h1>📊 Weekly Analytics Report</h1>
      <p>${report_period}</p>
    </div>

    <!-- Content -->
    <div class="content">
      <div class="greeting">
        Hi ${user_name},<br><br>
        Here's your weekly analytics summary for <strong>${studio_name}</strong>. See how your games performed this week and discover actionable insights to grow your player base.
      </div>

      <!-- Key Metrics -->
      <div class="stat-grid">
        <div class="stat-card">
          <div class="stat-label">Total Players</div>
          <div class="stat-value">${formatNumber(total_players)}</div>
          <div class="stat-subtext">This week</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Total Sessions</div>
          <div class="stat-value">${formatNumber(total_sessions)}</div>
          <div class="stat-subtext">This week</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Avg Session</div>
          <div class="stat-value">${avg_session_duration}m</div>
          <div class="stat-subtext">Duration</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Retention</div>
          <div class="stat-value">${retention_summary.day7}%</div>
          <div class="stat-subtext">Day 7</div>
        </div>
      </div>

      ${top_game ? `
      <!-- Top Performing Game -->
      <div class="section">
        <h3 class="section-title">
          ⭐ Top Performing Game
        </h3>
        <div class="game-item">
          <div class="game-name">
            ${top_game.title}
            <span class="badge badge-success">Best This Week</span>
          </div>
          <div class="game-stats">
            <span>👥 ${formatNumber(top_game.players)} players</span>
            <span>🎮 ${formatNumber(top_game.sessions)} sessions</span>
            <span>⭐ ${top_game.rating}/5</span>
          </div>
        </div>
      </div>
      ` : ''}

      ${games_summary && games_summary.length > 0 ? `
      <!-- Games Summary -->
      <div class="section">
        <h3 class="section-title">
          🎮 Your Games This Week
        </h3>
        ${games_summary.map(game => `
          <div class="game-item">
            <div class="game-name">${game.title}</div>
            <div class="game-stats">
              <span>👥 ${formatNumber(game.players)} players</span>
              <span>🎮 ${formatNumber(game.sessions)} sessions</span>
              <span>⏱️ ${game.avg_duration}m avg</span>
            </div>
          </div>
        `).join('')}
      </div>
      ` : ''}

      ${peak_hours ? `
      <!-- Peak Hours -->
      <div class="section">
        <h3 class="section-title">
          ⏰ Peak Activity Hours
        </h3>
        <div class="peak-hours-box">
          <div class="peak-hours-label">Best Time to Release Updates</div>
          <div class="peak-hours-time">${peak_hours.time}</div>
          <div class="peak-hours-day">${peak_hours.day}</div>
        </div>
        <p style="font-size: 13px; color: #94a3b8; margin-top: 12px; text-align: center;">
          ${peak_hours.message}
        </p>
      </div>
      ` : ''}

      ${insights && insights.length > 0 ? `
      <!-- Actionable Insights -->
      <div class="section">
        <h3 class="section-title">
          💡 Actionable Insights
        </h3>
        ${insights.map(insight => `
          <div class="insight-card insight-${insight.type}">
            <div class="insight-title">${insight.title}</div>
            <div class="insight-message">${insight.message}</div>
          </div>
        `).join('')}
      </div>
      ` : ''}

      <!-- CTA Button -->
      <center>
        <a href="${dashboard_url}" class="cta-button">
          View Full Analytics Dashboard →
        </a>
      </center>

      <div style="font-size: 13px; color: #64748b; text-align: center; margin-top: 20px; line-height: 1.6;">
        This is your weekly analytics report from <strong style="color: #6366f1;">Solis Games</strong> - the only platform that provides this level of intelligence about your games.
      </div>
    </div>

    <!-- Footer -->
    <div class="footer">
      <p class="footer-text">
        <strong>Solis Games - Studio Intelligence Hub</strong><br>
        Advanced analytics intelligence no other platform offers
      </p>
      <p class="footer-text">
        <a href="${dashboard_url}/settings" class="footer-link">Manage Email Preferences</a> |
        <a href="https://solisgames.com" class="footer-link">Visit Platform</a>
      </p>
      <p class="footer-text">
        © 2025 Solis Games. All rights reserved.
      </p>
    </div>
  </div>
</body>
</html>
  `;
};

// Helper function to format numbers with commas
function formatNumber(num) {
  if (!num && num !== 0) return '0';
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

// Generate plain text version for email clients that don't support HTML
export const generateAnalyticsReportText = (data) => {
  const {
    studio_name,
    user_name,
    report_period,
    games_summary,
    top_game,
    total_players,
    total_sessions,
    avg_session_duration,
    retention_summary,
    peak_hours,
    insights,
    dashboard_url
  } = data;

  let text = `
SOLIS GAMES - WEEKLY ANALYTICS REPORT
${report_period}

Hi ${user_name},

Here's your weekly analytics summary for ${studio_name}.

KEY METRICS THIS WEEK
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
• Total Players: ${formatNumber(total_players)}
• Total Sessions: ${formatNumber(total_sessions)}
• Avg Session Duration: ${avg_session_duration} minutes
• Day 7 Retention: ${retention_summary.day7}%

`;

  if (top_game) {
    text += `
TOP PERFORMING GAME
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
${top_game.title}
• ${formatNumber(top_game.players)} players
• ${formatNumber(top_game.sessions)} sessions
• ${top_game.rating}/5 rating

`;
  }

  if (games_summary && games_summary.length > 0) {
    text += `
YOUR GAMES THIS WEEK
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
${games_summary.map(game => `
${game.title}
• ${formatNumber(game.players)} players
• ${formatNumber(game.sessions)} sessions
• ${game.avg_duration}m avg duration
`).join('\n')}

`;
  }

  if (peak_hours) {
    text += `
PEAK ACTIVITY HOURS
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Best Time: ${peak_hours.time} on ${peak_hours.day}
${peak_hours.message}

`;
  }

  if (insights && insights.length > 0) {
    text += `
ACTIONABLE INSIGHTS
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
${insights.map((insight, idx) => `${idx + 1}. ${insight.title}\n   ${insight.message}`).join('\n\n')}

`;
  }

  text += `
View your full analytics dashboard:
${dashboard_url}

This is your weekly analytics report from Solis Games - the only platform that provides this level of intelligence about your games.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Solis Games - Studio Intelligence Hub
Advanced analytics intelligence no other platform offers

Manage email preferences: ${dashboard_url}/settings
© 2025 Solis Games. All rights reserved.
`;

  return text;
};
